import React, { useState } from 'react';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly index: number;
  /** Drag payload type. Distinct per list so a row never drops into the line set, or vice versa. */
  readonly dragType: string;
  readonly label: string;
  readonly className?: string;
  /** Double-click on the row itself. Used to insert a line without hunting for its button. */
  readonly onDoubleClick?: () => void;
  readonly disabled?: boolean;
  readonly onReorder: (fromIndex: number, toIndex: number) => void;
  readonly children: React.ReactNode;
}
// Same rule as StudioColumnSlot: a drag must not start on an interactive control, or its clicks,
// selection and keyboard stop working. The rest of the card is the drag surface.
const CONTROL_SELECTOR = 'input, select, textarea, button, a, [role="button"], [role="listbox"], [role="combobox"], [contenteditable="true"]';
export const StudioReorderSlot: React.FC<Props> = ({ index, dragType, label, className, disabled = false, onReorder, onDoubleClick, children }) => {
  const [dragging, setDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(false);
  const isReorderDrag = (event: React.DragEvent) => event.dataTransfer.types.includes(dragType);
  const classes = [className, styles.reorderSlot, dragging && styles.dragging, dropTarget && styles.dropTarget].filter(Boolean).join(' ');
  return (
    <div
      className={classes}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      role="group"
      aria-label={label}
      draggable={!disabled}
      title={disabled ? undefined : 'Drag to reorder'}
      onDragStart={
        disabled
          ? undefined
          : (event) => {
              if ((event.target as HTMLElement).closest(CONTROL_SELECTOR)) {
                event.preventDefault();
                return;
              }
              event.dataTransfer.setData(dragType, String(index));
              event.dataTransfer.effectAllowed = 'move';
              setDragging(true);
            }
      }
      onDragEnd={() => {
        setDragging(false);
        setDropTarget(false);
      }}
      onDragOver={(event) => {
        if (!isReorderDrag(event)) return;
        // Only claim the event for a reorder drag: a palette drag must still fall through to the
        // shelf, which is what adds a new field.
        event.preventDefault();
        setDropTarget(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropTarget(false);
      }}
      onDrop={(event) => {
        setDropTarget(false);
        if (!isReorderDrag(event)) return;
        event.preventDefault();
        event.stopPropagation();
        const from = Number(event.dataTransfer.getData(dragType));
        if (Number.isInteger(from) && from !== index) onReorder(from, index);
      }}
    >
      {children}
    </div>
  );
};
